<script setup>
    import { computed } from 'vue';
    import SgaStepHeader from './SgaStepHeader.vue';

    const props = defineProps({
        entitlements: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        },
        selectedEntitlementId: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['select']);

    /**
     * Filter entitlements to show only those with subscriberNumber > 0
     * Handles edge cases: non-array values, null, undefined
     */
    const filteredEntitlements = computed(() => {
        // Comprobación de seguridad: asegurar que props.entitlements es un array
        if (!Array.isArray(props.entitlements)) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('SeleccionarEntitlementSga: entitlements is not an array', props.entitlements);
            }
            return [];
        }
        // Filtrar por subscriberNumber > 0 y que tenga seatDetails.sector definido
        return props.entitlements.filter(e =>
            e.subscriberNumber &&
            e.subscriberNumber > 0 &&
            e.seatDetails &&
            e.seatDetails.zoneName
        );
    });

    /**
     * Handle entitlement selection
     */
    function handleSelect(entitlement) {
        emit('select', entitlement);
    }

    /**
     * Check if an entitlement is currently selected
     */
    function isSelected(id) {
        return props.selectedEntitlementId === id;
    }

    /**
     * Determine if entitlement is active
     */
    function isActive(entitlement) {
        return entitlement.status === 'Active';
    }
</script>

<template>
    <b-container class="seleccionar-entitlement-sga">
        <SgaStepHeader
            :title="$t('front.sga.checkout.derecho_titulo')"
            :description="$t('front.sga.checkout.derecho_descripcion')"
        />
        
        <!-- Loading State -->
        <div v-if="loading" class="tw:grid tw:grid-cols-1 tw:gap-4">
            <div 
                v-for="i in 2" 
                :key="`skeleton-${i}`"
                data-test-skeleton
                class="tw:animate-pulse tw:p-6 tw:rounded-2xl tw:bg-gray-100 tw:border-2 tw:border-gray-200"
            >
                <div class="tw:h-5 tw:w-48 tw:bg-gray-300 tw:rounded tw:mb-3"></div>
                <div class="tw:h-4 tw:w-32 tw:bg-gray-300 tw:rounded"></div>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger tw:p-4 tw:rounded-lg tw:bg-red-50 tw:border-l-4 tw:border-red-600">
            <p class="tw:text-red-800">{{ error }}</p>
        </div>

        <!-- Grid State -->
        <div v-else class="tw:grid tw:grid-cols-1 tw:gap-6 md:tw:grid-cols-2 lg:tw:grid-cols-3">
            <div 
                v-for="entitlement in filteredEntitlements" 
                :key="entitlement.id"
                data-test-entitlement-item
                :class="{
                    'tw:ring-2 tw:ring-offset-2 tw:ring-[var(--color-site)] tw:shadow-lg': isSelected(entitlement.id),
                    'tw:border-gray-100 tw:shadow-sm': !isSelected(entitlement.id)
                }"
                class="
                    tw:relative tw:flex tw:flex-col
                    tw:p-6 tw:rounded-2xl tw:bg-white
                    tw:border-2 tw:cursor-pointer
                    tw:transition-all tw:duration-300 tw:ease-out
                    tw:hover:shadow-md tw:hover:border-gray-200 tw:hover:-translate-y-1
                "
                @click="handleSelect(entitlement)"
            >
                <!-- Selection Indicator -->
                <div 
                    v-if="isSelected(entitlement.id)"
                    class="tw:absolute tw:top-3 tw:right-3 tw:transition-opacity tw:duration-200"
                >
                    <div class="tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-6 tw:rounded-full tw:bg-green-500 tw:text-white tw:shadow-md">
                        <svg xmlns="http://www.w3.org/2000/svg" class="tw:w-4 tw:h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                    </div>
                </div>

                <div class="tw:flex tw:items-start tw:gap-4 tw:flex-1 tw:flex-col">
                    <!-- Icon and Header -->
                    <div class="tw:flex tw:items-start tw:gap-4 tw:w-full">
                        <!-- Icon based on type -->
                        <div class="tw:w-12 tw:h-12 tw:rounded-xl tw:bg-gradient-to-br tw:from-blue-50 tw:to-blue-100 tw:flex tw:items-center tw:justify-center tw:flex-shrink-0">
                            <svg v-if="entitlement.type === 'Subscription'" xmlns="http://www.w3.org/2000/svg" class="tw:w-6 tw:h-6 tw:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="tw:w-6 tw:h-6 tw:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                        </div>

                        <div class="tw:flex-1">
                            <div class="tw:flex tw:items-baseline tw:gap-2 tw:mb-1">
                                <h4 class="tw:text-base tw:font-bold tw:text-gray-900 tw:leading-tight">
                                    {{ entitlement.productName || $t('front.sga.abono') }}
                                </h4>
                                <span class="tw:text-[10px] tw:px-1.5 tw:py-0.5 tw:rounded tw:bg-gray-100 tw:text-gray-600 tw:font-mono tw:font-bold">
                                    #{{ entitlement.subscriberNumber }}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Seat details -->
                    <div v-if="entitlement.seatDetails?.zoneName" class="tw:flex tw:items-start tw:gap-1.5 tw:text-sm tw:text-gray-600 tw:w-full">
                        <svg xmlns="http://www.w3.org/2000/svg" class="tw:w-4 tw:h-4 tw:text-gray-400 tw:flex-shrink-0 tw:mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span class="tw:flex-1">
                            {{ entitlement.seatDetails.zoneName }}
                            <template v-if="entitlement.seatDetails.sector"> · {{ entitlement.seatDetails.sector }}</template>
                            <template v-if="entitlement.seatDetails.row"> · {{ $t('front.sga.fila') }} {{ entitlement.seatDetails.row }}</template>
                            <template v-if="entitlement.seatDetails.seatNumber"> · {{ $t('front.sga.butaca') }} {{ entitlement.seatDetails.seatNumber }}</template>
                        </span>
                    </div>
                </div>

                <!-- Footer info: Status & Expiration -->
                <div class="tw:flex tw:items-center tw:gap-3 tw:pt-3 tw:border-t tw:border-gray-100 tw:mt-3">
                    <span 
                        :class="isActive(entitlement) ? 'tw:bg-green-50 tw:text-green-700' : 'tw:bg-gray-50 tw:text-gray-500'"
                        class="tw:text-[11px] tw:font-semibold tw:px-2.5 tw:py-1 tw:rounded-full tw:inline-block"
                    >
                        {{ entitlement.status || $t('front.sga.status_unknown') }}
                    </span>
                    <span v-if="entitlement.expirationDate" class="tw:text-[10px] tw:text-gray-500 tw:flex-1">
                        {{ $t('front.sga.expira') }}: <span class="tw:font-medium">{{ entitlement.expirationDate }}</span>
                    </span>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="filteredEntitlements.length === 0" class="tw:col-span-full tw:p-12 tw:text-center">
                <div data-test-empty-state class="tw:rounded-2xl tw:border-2 tw:border-dashed tw:border-gray-300 tw:p-8 tw:bg-gray-50">
                    <svg xmlns="http://www.w3.org/2000/svg" class="tw:w-12 tw:h-12 tw:text-gray-400 tw:mx-auto tw:mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p class="tw:text-gray-600 tw:text-sm">{{ $t('front.sga.checkout.derecho_vacio') }}</p>
                </div>
            </div>
        </div>
    </b-container>
</template>

<style scoped>
.seleccionar-entitlement-sga {
    min-height: 200px;
}
</style>